import { useCallback, useRef } from "react";
import html2canvas from "html2canvas";
import { SHAPE, TEXT } from "../constants/editor";

/* ---------------- UTILITIES ---------------- */

function hexToRgb(hex) {
  let clean = hex.replace("#", "");

  if (clean.length === 3)
    clean = clean
      .split("")
      .map(c => c + c)
      .join("");

  const num = parseInt(clean, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function parseRgbString(rgb) {
  if (!rgb) return null;
  const match = rgb.match(/\d+/g);
  if (!match) return null;
  return { r: +match[0], g: +match[1], b: +match[2] };
}

function srgbToLinear(v) {
  v = v / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function getLuminance({ r, g, b }) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

function getContrastRatio(l1, l2) {
  const L1 = Math.max(l1, l2);
  const L2 = Math.min(l1, l2);
  return (L1 + 0.05) / (L2 + 0.05);
}

function getTextCategory(fontSize, fontWeight) {
  const isBold = fontWeight >= 700;
  return fontSize >= 18 || (isBold && fontSize >= 14) ? "large" : "normal";
}

function evaluate(ratio, category) {
  const rules = {
    normal: { AA: 4.5, AAA: 7 },
    large: { AA: 3, AAA: 4.5 },
  };

  const t = rules[category];

  let grade = "FAIL";
  if (ratio >= t.AAA) grade = "AAA";
  else if (ratio >= t.AA) grade = "AA";

  return {
    ratio: Number(ratio.toFixed(2)),
    grade,
    passes: {
      AA: ratio >= t.AA,
      AAA: ratio >= t.AAA,
    },
  };
}

/* ---------------- ROOT BACKGROUND (DOM fallback, used only when
   the element truly covers the entire canvas) ---------------- */

function getRootBackground(root) {
  if (!root || !(root instanceof Element)) {
    return { r: 255, g: 255, b: 255 };
  }

  let el = root;

  while (el) {
    if (!(el instanceof Element)) break;

    try {
      const bg = window.getComputedStyle(el).backgroundColor;

      if (bg && bg !== "transparent" && !bg.includes("0, 0, 0, 0")) {
        const parsed = parseRgbString(bg);
        if (parsed) return parsed;
      }
    } catch (e) {}

    el = el.parentElement;
  }

  try {
    const bodyBg = window.getComputedStyle(document.body).backgroundColor;
    return parseRgbString(bodyBg) || { r: 255, g: 255, b: 255 };
  } catch (e) {
    return { r: 255, g: 255, b: 255 };
  }
}

/* ---------------- SAMPLING (clamped-to-canvas version) ---------------- */

function sample(canvas, element, rootRect, isShape) {
  const ctx = canvas.getContext("2d");
  const rect = element.getBoundingClientRect();
  const samples = [];

  const canvasW = canvas.width;
  const canvasH = canvas.height;

  function safeGetPixel(px, py) {
    const safeX = Math.max(0, Math.min(canvasW - 1, Math.floor(px)));
    const safeY = Math.max(0, Math.min(canvasH - 1, Math.floor(py)));
    const d = ctx.getImageData(safeX, safeY, 1, 1).data;
    return { r: d[0], g: d[1], b: d[2] };
  }

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  // Element bounds relative to the canvas/root
  const x = rect.left - rootRect.left;
  const y = rect.top - rootRect.top;
  const w = rect.width;
  const h = rect.height;

  // Element doesn't intersect the canvas at all — nothing to sample there
  const noOverlap = x >= canvasW || y >= canvasH || x + w <= 0 || y + h <= 0;
  if (noOverlap) {
    return [getRootBackground(element || document.body)];
  }

  // Small epsilon so a clamped point sitting exactly on the element's
  // edge still counts as "on the element" and gets rejected
  const EPS = 0.5;
  function isInsideElement(px, py) {
    return px >= x - EPS && px <= x + w + EPS && py >= y - EPS && py <= y + h + EPS;
  }

  // Clamp a candidate point into canvas bounds. Only keep it if, after
  // clamping, it does NOT land back inside the element itself — that
  // would mean we're sampling the object's own fill, not what's behind it.
  function pushClamped(px, py) {
    const cx = clamp(px, 0, canvasW - 1);
    const cy = clamp(py, 0, canvasH - 1);

    if (!isInsideElement(cx, cy)) {
      samples.push(safeGetPixel(cx, cy));
    }
  }

  if (isShape) {
    const pad = 4;

    const points = [
      [x - pad, y],
      [x + w + pad, y],
      [x, y - pad],
      [x, y + h + pad],
      [x + w + pad, y + h],
    ];

    points.forEach(([px, py]) => pushClamped(px, py));
  } else {
    const steps = 5;
    const offset = 6;

    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);

      const points = [
        [x + t * w, y - offset],
        [x + t * w, y + h + offset],
        [x - offset, y + t * h],
        [x + w + offset, y + t * h],
      ];

      points.forEach(([px, py]) => pushClamped(px, py));
    }
  }

  if (!samples.length) {
    // Every clamped candidate still landed inside the element — it
    // genuinely covers the whole visible canvas. No pixel-sampled
    // background exists, so fall back to the CSS-derived one.
    return [getRootBackground(element || document.body)];
  }

  return samples;
}

/* ---------------- REST OF YOUR CODE (unchanged) ---------------- */

function getMedian(samples) {
  const mid = Math.floor(samples.length / 2);
  const sort = arr => arr.sort((a, b) => a - b);

  return {
    r: sort(samples.map(s => s.r))[mid],
    g: sort(samples.map(s => s.g))[mid],
    b: sort(samples.map(s => s.b))[mid],
  };
}

function applyOpacity(textRgb, bgRgb, opacity) {
  return {
    r: textRgb.r * opacity + bgRgb.r * (1 - opacity),
    g: textRgb.g * opacity + bgRgb.g * (1 - opacity),
    b: textRgb.b * opacity + bgRgb.b * (1 - opacity),
  };
}

function isSameColor(c1, c2) {
  return Math.abs(c1.r - c2.r) + Math.abs(c1.g - c2.g) + Math.abs(c1.b - c2.b) < 10;
}

/* ---------------- HOOK  ---------------- */

export function useAutoContrastChecker() {
  const timeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  const checkContrast = useCallback((elType, element, textColor, root) => {
    return new Promise(resolve => {
      if (!(element instanceof Element) || !(root instanceof Element)) {
        resolve(null);
        return;
      }

      const currentRequestId = ++requestIdRef.current;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          const textRgb = textColor.startsWith("#") ? hexToRgb(textColor) : parseRgbString(textColor);

          if (!textRgb) return resolve(null);

          const innerEl = element.querySelector(".dhp-widget-inner") || element;
          const computed = window.getComputedStyle(innerEl);

          /* -------- TEXT BACKGROUND PRIORITY -------- */

          let bg = null;

          if (elType === TEXT) {
            const bgColor = computed.backgroundColor;

            if (bgColor && bgColor !== "transparent" && !bgColor.includes("0, 0, 0, 0")) {
              const parsed = parseRgbString(bgColor);
              if (parsed) bg = parsed;
            }
          }

          /* -------- FALLBACK SAMPLING -------- */

          if (!bg) {
            html2canvas(root, {
              useCORS: true,
              backgroundColor: null,
              scale: 1,
            }).then(canvas => {
              const rootRect = root.getBoundingClientRect();
              const isShape = elType === SHAPE;

              bg = getMedian(sample(canvas, element, rootRect, isShape));
              finish(bg);
            });
          } else {
            finish(bg);
          }

          function finish(bg) {
            let opacity = 1;

            const inner = element.querySelector(".dhp-widget-inner");
            if (inner) {
              const style = window.getComputedStyle(inner);
              opacity = parseFloat(style.opacity || "1");
            }

            let finalTextRgb = textRgb;

            if (opacity < 1) {
              finalTextRgb = applyOpacity(textRgb, bg, opacity);
            }

            /* -------- OUTLINE HANDLING -------- */
            const textNode = element.querySelector(".primary-text-widget") || element;
            const style = window.getComputedStyle(textNode);

            const strokeWidth = parseFloat(style.webkitTextStrokeWidth || "0");
            const strokeColor = style.webkitTextStrokeColor;
            const strokeRgb = parseRgbString(strokeColor);

            let effectiveTextRgb = finalTextRgb;

            if (elType === TEXT && strokeWidth > 0 && strokeRgb) {
              if (strokeWidth >= 1) effectiveTextRgb = strokeRgb;

              if (isSameColor(strokeRgb, bg)) {
                return resolve({
                  textColor,
                  backgroundColor: `rgb(${bg.r}, ${bg.g}, ${bg.b})`,
                  textSize: "normal",
                  ratio: 1,
                  grade: "FAIL",
                  passes: { AA: false, AAA: false },
                });
              }
            }

            const ratio = getContrastRatio(getLuminance(effectiveTextRgb), getLuminance(bg));

            let category = "large";

            if (elType === TEXT) {
              const fontSize = parseFloat(textNode.style.fontSize || "16");
              const fontWeight = parseInt(textNode.style.fontWeight || "400", 10);
              category = getTextCategory(fontSize, fontWeight);
            }

            if (currentRequestId !== requestIdRef.current) return;

            resolve({
              textColor,
              backgroundColor: `rgb(${Math.round(bg.r)}, ${Math.round(bg.g)}, ${Math.round(bg.b)})`,
              textSize: category,
              ...evaluate(ratio, category),
            });
          }
        });
      }, 80);
    });
  }, []);

  /* ---------------- SIMPLE (TABLE / SOLID COLORS) ---------------- */
  const checkSimpleContrast = (textColor, backgroundColor, fontSize = 16, fontWeight = 400) => {
    const textRgb = textColor.startsWith("#") ? hexToRgb(textColor) : parseRgbString(textColor);

    const bgRgb = backgroundColor.startsWith("#") ? hexToRgb(backgroundColor) : parseRgbString(backgroundColor);

    if (!textRgb || !bgRgb) return null;

    const textLum = getLuminance(textRgb);
    const bgLum = getLuminance(bgRgb);
    const ratio = getContrastRatio(textLum, bgLum);
    const category = getTextCategory(fontSize, fontWeight);

    return {
      textColor,
      backgroundColor,
      textSize: category,
      ...evaluate(ratio, category),
    };
  };

  return { checkContrast, checkSimpleContrast };
}
